import { InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useFormField, useDebouncedValue } from '@frontend/design-system';
import { AddToListButton } from '../components/add-to-list-button';
import { EmptyListIllustration } from '../components/empty-list-illustration';
import { SearchBar } from '../components/search-bar';
import { SpinnerLoader } from '../components/spinning-loader';
import { useQuickFillRecipients } from '../hooks';
import { useQuickFillRecipientShallowStore } from '../store';
import { QuickFillListItem } from './list-item';

type Props = {
  locationId: string;
  hasMultipleLocations?: boolean;
};

export const QuickFillList = ({ locationId, hasMultipleLocations = false }: Props) => {
  const { t } = useTranslation('schedule-quick-fill');
  const searchFieldProps = useFormField({ type: 'text', value: '' });
  const searchTerm = useDebouncedValue(searchFieldProps.value);

  const infiniteQuery = useQuickFillRecipients(locationId, searchTerm);
  const { recipientCounts } = useQuickFillRecipientShallowStore('recipientCounts');
  const { filteredCount, totalCount } = recipientCounts[locationId] || {};

  const isEmptyList = infiniteQuery.data?.pages[0].rows?.length === 0;

  // Only show loader for component for initial list load, not for searched or subsequent loads
  if (infiniteQuery.isLoading && isEmptyList && !searchTerm) {
    return <SpinnerLoader height={hasMultipleLocations ? 260 : '100%'} />;
  }

  return (
    <div css={{ backgroundColor: theme.colors.white }}>
      {isEmptyList && !searchTerm ? (
        <EmptyListIllustration imageSize={336} />
      ) : (
        <>
          <div css={{ padding: theme.spacing(2, 3, 2, 2) }}>
            <SearchBar
              showTally={!hasMultipleLocations}
              filteredCount={filteredCount}
              totalCount={totalCount}
              searchFieldProps={searchFieldProps}
            />
          </div>
          <InfinitePaginatedList
            height={260} // Height of 4 items in list
            emptyStateConfig={{
              header: t('No Quick Fill recipients found'),
              disableGraphic: true,
              textProps: { color: 'subdued' },
            }}
            infiniteQueryProps={infiniteQuery}
            renderListItem={({ listItem }) => (
              <QuickFillListItem key={listItem.personId} recipient={listItem} locationId={locationId} />
            )}
          />
        </>
      )}
      <AddToListButton showSeparator locationId={locationId} position={isEmptyList ? 'center' : 'left'} />
    </div>
  );
};
